import { useQuery } from "@tanstack/react-query";
import { api } from "../../../../singleton/axios";

export interface AnalystEstimate {
  symbol: string;
  date: string;
  revenueLow: number;
  revenueHigh: number;
  revenueAvg: number;
  ebitdaLow: number;
  ebitdaHigh: number;
  ebitdaAvg: number;
  ebitLow: number;
  ebitHigh: number;
  ebitAvg: number;
  netIncomeLow: number;
  netIncomeHigh: number;
  netIncomeAvg: number;
  sgaExpenseLow: number;
  sgaExpenseHigh: number;
  sgaExpenseAvg: number;
  epsAvg: number;
  epsHigh: number;
  epsLow: number;
  numAnalystsRevenue: number;
  numAnalystsEps: number;
}

export type AnalystEstimatesData = AnalystEstimate[];

interface UseAnalystEstimatesOptions {
  period?: "annual" | "quarter";
  limit?: number;
}

export function useAnalystEstimates(
  symbol: string | undefined,
  options: UseAnalystEstimatesOptions = {}
) {
  const { period = "annual", limit = 5 } = options;

  return useQuery<AnalystEstimatesData, Error>({
    queryKey: ["analystEstimates", symbol, period, limit],
    queryFn: async () => {
      if (!symbol) throw new Error("Symbol is required");

      const { data } = await api.get<AnalystEstimatesData>(
        "/analyst-estimates",
        {
          params: { symbol, period, limit },
        }
      );
      return data;
    },
    enabled: !!symbol,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    retry: (failureCount, error) => {
      // Don't retry on 404s - no estimates data exists for this stock
      if ((error as any)?.response?.status === 404) return false;
      return failureCount < 2;
    },
  });
}
